import {
    savedQueryResponseMapper, buildQueryModel,
} from "../rest/mappers/saved-query-mapper";
import {RouteConstants} from "../utils/route-constants";
import 'angular/rest/connectors.rest.service';
import 'angular/externalsync/controllers';
import {YasguiComponentDirectiveUtil} from "../core/directives/yasgui-component/yasgui-component-directive.util";
import {QueryType} from "../models/ontotext-yasgui/query-type";
import {ConnectorCommand} from "../models/connectors/connector-command";
import {BeforeUpdateQueryResult, BeforeUpdateQueryResultStatus} from "../models/ontotext-yasgui/before-update-query-result";
import {EventDataType} from "../models/ontotext-yasgui/event-data-type";
import {decodeHTML} from "../../../app";
import {toBoolean} from "../utils/string-utils";
import {VIEW_SPARQL_EDITOR} from "../models/sparql/constants";
import {CancelAbortingQuery} from "../models/sparql/cancel-aborting-query";
import {QueryMode} from "../models/ontotext-yasgui/query-mode";
import 'angular/core/services/event-emitter-service';
import {LoggerProvider} from "../core/services/logger-provider";
import {
    ParentWindowMessageService,
    navigateTo,
    openInNewTab,
    LanguageContextService,
    ServiceProvider,
    EventService,
    EventName,
    SecurityContextService,
    GraphConfigService,
    service,
    REPOSITORY_ID_PARAM,
    GuidesService,
    WindowService,
} from "@ontotext/workbench-api";
import {YASGUI_OPERATION, YASGUI_OPERATION_TYPE, URL_PLUGIN_NAME_TO_PLUGIN_NAME_MAPPING} from "./constants";
import {GeoPluginConfiguration} from '../models/ontotext-yasgui/plugin-configuration';

const modules = [
    'ui.bootstrap',
    'graphdb.framework.rest.connectors.service',
    'graphdb.framework.externalsync.controllers',
    'graphdb.framework.utils.event-emitter-service',
    'graphdb.framework.utils.localstorageadapter',
];

const logger = LoggerProvider.logger;

angular
    .module('graphdb.framework.sparql-editor.controllers', modules)
    .controller('SparqlEditorCtrl', SparqlEditorCtrl);

SparqlEditorCtrl.$inject = [
    '$rootScope',
    '$scope',
    '$q',
    '$location',
    '$languageService',
    '$repositories',
    '$uibModal',
    'toastr',
    '$translate',
    'SparqlRestService',
    'ConnectorsRestService',
    'ModalService',
    'MonitoringRestService',
    'EventEmitterService',
    'LocalStorageAdapter',
    'LSKeys'];

function SparqlEditorCtrl($rootScope,
                          $scope,
                          $q,
                          $location,
                          $languageService,
                          $repositories,
                          $uibModal,
                          toastr,
                          $translate,
                          SparqlRestService,
                          ConnectorsRestService,
                          ModalService,
                          MonitoringRestService,
                          EventEmitterService,
                          LocalStorageAdapter,
                          LSKeys) {
    const securityContextService = service(SecurityContextService);
    const guidesService = service(GuidesService);
    const parentWindowMessageService = service(ParentWindowMessageService);
    const graphConfigService = service(GraphConfigService);

    this.repository = '';

    const QUERY_EDITOR_ID = '#query-editor';
    let activeRepository = $repositories.getActiveRepository();
    let isOntopRepo = $repositories.isActiveRepoOntopType();
    // This is used to determine whether the view is embedded in another application. When embedded, we want to hide
    // some elements and disable the "go to home" functionality, as it doesn't make sense in that context.
    $scope.embedded = $location.search().embedded;
    $scope.isExternalClickHandlerOperationEnabled = YASGUI_OPERATION.EXTERNAL_CLICK_HANDLER === $location.search().yasguiOperation;
    $scope.selectedPlugin = URL_PLUGIN_NAME_TO_PLUGIN_NAME_MAPPING[$location.search().pluginName];
    /**
     * @type {OntotextYasguiConfig}
     */
    $scope.yasguiConfig = undefined;
    $scope.savedQueryConfig = undefined;
    $scope.prefixes = {};
    $scope.inferUserSetting = true;
    $scope.sameAsUserSetting = true;

    const tabIdToConnectorProgressModalMapping = new Map();
    let internallyReloaded = false;

    // =========================
    // Public functions
    // =========================
    /**
     * Updates the Yasgui configuration
     * @param {boolean} clearYasguiState if set to true, the Yasgui will reinitialize and clear all tab results. Queries will remain.
     */
    $scope.updateConfig = (clearYasguiState) => {
        const yasrToolbarPlugins = $scope.embedded ? [] : [exploreVisualGraphYasrToolbarElementBuilder];
        const yasguiConfig = {
            endpoint: getEndpoint,
            componentId: VIEW_SPARQL_EDITOR,
            prefixes: $scope.prefixes,
            infer: isOntopRepo || $scope.inferUserSetting,
            sameAs: isOntopRepo || $scope.sameAsUserSetting,
            yasrToolbarPlugins,
            selectedPlugin: $scope.selectedPlugin,
            beforeUpdateQuery: getBeforeUpdateQueryHandler(),
            outputHandlers: new Map([
                [EventDataType.QUERY_EXECUTED, queryExecutedHandler],
                [EventDataType.REQUEST_ABORTED, requestAbortedHandler],
            ]),
            clearState: clearYasguiState ?? false,
            pluginsConfigurations: {
                geo: getGeoPluginConfiguration(),
            },
        };

        if ($scope.embedded) {
            yasguiConfig.yasrFullscreen = true;
            yasguiConfig.downloadAsOn = false;
        }
        $scope.yasguiConfig = yasguiConfig;
    };

    $scope.getActiveRepositoryNoError = () => {
        return activeRepository;
    };

    // =========================
    // Private functions
    // =========================
    /**
     * Callback function triggered when a user clicks on a feature in the geo plugin.
     * Sends a message to the parent window using `ParentWindowMessageService`.
     * @returns {GeoPluginConfiguration} the configuration for the Geo plugin.
     */
    const getGeoPluginConfiguration = () => {
        const geoPluginConfig = new GeoPluginConfiguration();

        if ($scope.embedded && $scope.isExternalClickHandlerOperationEnabled) {
            geoPluginConfig.onFeatureClick = onFeatureClickHandler;
        }
        return geoPluginConfig;
    };


    /**
     * Callback function triggered when a user clicks on a feature in the geo plugin.
     * Sends a message to the parent window using `ParentWindowMessageService`.
     *
     * @param {Object} featurePayload - The data of the clicked feature.
     * @param {string} [featurePayload.id] - The unique ID of the feature (optional, depending on payload structure).
     * @param {Object} [featurePayload.geometry] - Geometry properties of the feature (optional).
     */
    const onFeatureClickHandler = (featurePayload) => {
        parentWindowMessageService.postMessage({
            type: YASGUI_OPERATION_TYPE.FEATURE_CLICK,
            featurePayload,
        }, WindowService.getReferer());
    };


    const getYasqe = (yasgui) => {
        const tab = yasgui.getTab();
        if (!tab) {
            return;
        }
        return tab.getYasqe();
    };

    const getEndpoint = (yasgui) => {
        const yasqe = getYasqe(yasgui);
        if (!yasqe) {
            // this can happen if open saprql view for first time (browser local store is clear);
            return;
        }
        return $repositories.resolveSparqlEndpoint(yasqe.getQueryMode());
    };

    /**
     * Initializes the editor from the URL parameters.
     * @param {boolean} clearYasguiState if set to true, the Yasgui will reinitialize and clear all tab results. Queries will remain.
     * The default is false.
     */
    const initViewFromUrlParams = (clearYasguiState = false) => {
        $scope.updateConfig(clearYasguiState);
        const queryParams = $location.search();
        if (queryParams.hasOwnProperty(RouteConstants.savedQueryName)) {
            // init new tab from shared saved query link
            initTabFromSavedQuery(queryParams);
        } else if (queryParams.hasOwnProperty(RouteConstants.query)) {
            // init new tab from shared query link
            initTabFromSharedQuery(queryParams);
        } else if (guidesService.isActive()) {
            openNewTab();
        }
    };

    // ================================
    // =     Setup output handlers
    // ================================
    /**
     * Handles the "queryExecuted" event emitted by ontotext-yasgui. The event is fired immediately after the request is executed, whether it succeeds or fails.
     * @param {QueryExecutedEvent} queryExecutedRequest - the event payload.
     */
    const queryExecutedHandler = (queryExecutedRequest) => {
        const connectorProgressModal = tabIdToConnectorProgressModalMapping.get(queryExecutedRequest.tabId);
        if (connectorProgressModal) {
            connectorProgressModal.dismiss();
            tabIdToConnectorProgressModalMapping.delete(queryExecutedRequest.tabId);
        }
    };

    /**
     * Handles the "requestAborted" event emitted by the ontotext-yasgui. The event is fired when a request is aborted.
     *
     * @param {RequestAbortedEvent} requestAbortedEvent the event payload containing the request object and the query mode.
     */
    const requestAbortedHandler = (requestAbortedEvent) => {
        if (requestAbortedEvent && QueryMode.UPDATE !== requestAbortedEvent.queryMode) {
            const repository = requestAbortedEvent.getRepository();
            const currentTrackAlias = requestAbortedEvent.getQueryTrackAlias();
            if (repository && currentTrackAlias) {
                MonitoringRestService.deleteQuery(currentTrackAlias, repository);
            }
        }
    };

    // =========================
    // Private function
    // =========================

    const initTabFromSavedQuery = (queryParams) => {
        const savedQueryName = queryParams[RouteConstants.savedQueryName];
        const savedQueryOwner = queryParams[RouteConstants.savedQueryOwner];
        const isExecuteRequested = toBoolean(queryParams.execute);

        SparqlRestService.getSavedQuery(savedQueryName, savedQueryOwner).then((res) => {
            const savedQuery = savedQueryResponseMapper(res);
            openNewTab(savedQuery)
                .then(clearUrlParameters)
                .then(() => autoExecuteQueryIfRequested(isExecuteRequested));
        }).catch((err) => {
            toastr.error($translate.instant('query.editor.missing.saved.query.data.error', {
                savedQueryName: savedQueryName,
                error: getError(err),
            }));
        });
    };

    const initTabFromSharedQuery = (queryParams) => {
        const queryName = queryParams[RouteConstants.name];
        const query = queryParams[RouteConstants.query];
        const queryOwner = queryParams[RouteConstants.owner];
        const isExecuteRequested = toBoolean(queryParams.execute);
        const sharedQueryModel = buildQueryModel(query, queryName, queryOwner, true);
        openNewTab(sharedQueryModel)
            .then(clearUrlParameters)
            .then(() => autoExecuteQueryIfRequested(isExecuteRequested));
    };

    /**
     * Open a new tab with query described in <code>sparqlQuery</code>.
     *
     * @param {TabQueryModel} sparqlQuery
     *
     * @return {Promise<void>}
     */
    const openNewTab = (sparqlQuery) => {
        return YasguiComponentDirectiveUtil.getOntotextYasguiElementAsync(QUERY_EDITOR_ID)
            .then((yasguiComponent) => yasguiComponent.openTab(sparqlQuery))
            .then((tab) => YasguiComponentDirectiveUtil.highlightTabName(tab));
    };

    const clearUrlParameters = () => {
        internallyReloaded = true;
        const currentParams = $location.search();
        // Keep the repositoryId parameter (if any). This will prevent router event from being triggered again and
        // reinitializing the repositoryId param thus adding a new history entry.
        const repositoryId = currentParams[REPOSITORY_ID_PARAM];
        const searchTerm = repositoryId ? {repositoryId} : {};
        // If the Workbench is in embedded mode, it should remain embedded.
        if ($scope.embedded) {
            searchTerm.embedded = true;
        }
        $location.search(searchTerm);
        // Replace current URL without adding a new history entry
        $location.replace();
    };

    const autoExecuteQueryIfRequested = (isRequested) => {
        if (isRequested) {
            return YasguiComponentDirectiveUtil.getOntotextYasguiElementAsync(QUERY_EDITOR_ID)
                .then(getQueryMode)
                .then(confirmAndExecuteQuery);
        }
        return Promise.resolve();
    };

    const getQueryMode = (yasguiComponent) => {
        return yasguiComponent.getQueryMode()
            .then((queryMode) => {
                return {yasguiComponent, queryMode};
            });
    };

    const confirmAndExecuteQuery = ({yasguiComponent, queryMode}) => {
        if (queryMode !== 'update') {
            return yasguiComponent.query();
        }

        return new Promise((resolve) => {
            const title = $translate.instant('confirm.execute');
            const message = decodeHTML($translate.instant('query.editor.automatically.execute.update.warning'));
            ModalService.openConfirmation(title, message, () => resolve(yasguiComponent.query()), () => resolve());
        });
    };

    const setInferAndSameAs = (principal) => {
        $scope.inferUserSetting = principal?.appSettings.DEFAULT_INFERENCE;
        $scope.sameAsUserSetting = principal?.appSettings.DEFAULT_SAMEAS;
    };

    /**
     * Navigates to the Graph Visualizations page.
     *
     * @param yasr - YASR result renderer instance containing the current query context.
     * @param visGraphConfigId - Optional identifier of a saved graph visualization configuration.
     */
    const navigateToGraphsVisualizations = (yasr, visGraphConfigId) => {
        const paramsToParse = {
            query: yasr.yasqe.getValue(),
            sameAs: yasr.yasqe.getSameAs(),
            inference: yasr.yasqe.getInfer(),
        };

        if (visGraphConfigId) {
            paramsToParse.config = visGraphConfigId;
        }

        const navigate = function() {
            $location.path('graphs-visualizations').search(paramsToParse);
        };
        // This button is embedded in the Yasr toolbar, which is outside the Angular context, so we need to
        // manually trigger the navigation inside the Angular context. Otherwise, the navigation will be
        // triggered on the next digest cycle that might never happen. In our case there is a constant server
        // polling that triggers the digest cycle, so the navigation will be triggered, but it can lead to a
        // noticeable delay. By manually triggering the navigation inside the Angular context, we ensure that
        // the navigation is triggered immediately.
        if ($scope.$$phase || $rootScope.$$phase) {
            navigate();
        } else {
            $scope.$apply(navigate);
        }
    };

    /**
     * Builds an event handler for graph exploration actions emitted by the `onto-graph-explore-split-button` component.
     *
     * The handler interprets the event payload and routes the user accordingly:
     * - `create` - opens graph visualizations in a new tab
     * - `default` - navigates using the current query context
     * - `select` (with config) - navigates with a specific graph configuration
     *
     * @param yasr - YASR result renderer instance used to derive query context.
     * @returns Event handler function for `graphExplore` events.
     */
    const getExploreVisualGraphHandler = (yasr) => {
        return ({detail: payload}) => {
            if ('create' === payload.action) {
                openInNewTab('graphs-visualizations');
            } else if ('default' === payload.action) {
                navigateToGraphsVisualizations(yasr);
            } else if (payload.graphConfig) {
                navigateToGraphsVisualizations(yasr, payload.graphConfig.id);
            }
        };
    };

    /**
     * Factory for the YASR toolbar element that provides graph exploration capabilities.
     */
    const exploreVisualGraphYasrToolbarElementBuilder = {
        createElement: (yasr) => {
            const exploreVisualGraphButtonGroup = document.createElement('onto-graph-explore-split-button');
            exploreVisualGraphButtonGroup.classList.add('explore-visual-graph');
            exploreVisualGraphButtonGroup.fetchGraphConfigs = () => graphConfigService.getGraphConfigs().then((graphConfigs) => graphConfigs.getItems());
            exploreVisualGraphButtonGroup.label = $translate.instant('query.editor.visual.btn');
            exploreVisualGraphButtonGroup._exploreVisualGraphHandler = getExploreVisualGraphHandler(yasr);
            exploreVisualGraphButtonGroup.addEventListener('graphExplore', exploreVisualGraphButtonGroup._exploreVisualGraphHandler);
            return exploreVisualGraphButtonGroup;
        },
        updateElement: (element, yasr) => {
            element.classList.add('hidden');
            if (!yasr.hasResults()) {
                return;
            }
            const queryType = yasr.yasqe.getQueryType();

            if (QueryType.CONSTRUCT === queryType || QueryType.DESCRIBE === queryType) {
                element.classList.remove('hidden');
            }
        },
        getOrder: () => {
            return 2;
        },

        destroy(element) {
            if (element._exploreVisualGraphHandler) {
                element.removeEventListener('graphExplore', element._exploreVisualGraphHandler);
            }
            element.remove();
        },
    };

    const createParameter = (key, value) => {
        return {key, value};
    };

    const getCommandParameters = (response) => {
        return [createParameter('name', response.data.name)];
    };

    const toNoCommandResponse = () => {
        return new BeforeUpdateQueryResult(BeforeUpdateQueryResultStatus.SUCCESS);
    };

    const toHasNotSupport = (response) => {
        const parameters = [
            createParameter('connectorName', response.data.connectorName),
            createParameter('pluginName', response.data.pluginName),
        ];
        return new BeforeUpdateQueryResult(BeforeUpdateQueryResultStatus.ERROR, 'query.editor.inactive.plugin.warning.msg', parameters);
    };

    const createConnectorProgressDialog = (actionName, iri, connectorName) => {
        const progressScope = $scope.$new(true);
        progressScope.beingBuiltConnector = {
            percentDone: 0,
            status: {
                processedEntities: 0,
                estimatedEntities: 0,
                indexedEntities: 0,
                entitiesPerSecond: 0,
            },
            actionName,
            eta: "-",
            inline: false,
            iri,
            name: connectorName,
            doneCallback: function() {
                connectorProgressModal.dismiss('cancel');
            },
        };
        progressScope.getHumanReadableSeconds = $scope.getHumanReadableSeconds;

        const connectorProgressModal = $uibModal.open({
            templateUrl: 'pages/connectorProgress.html',
            controller: 'CreateProgressCtrl',
            size: 'lg',
            backdrop: 'static',
            scope: progressScope,
        });
        return connectorProgressModal;
    };

    const toCreateCommandResponse = (response, tabId) => {
        const connectorProgressModal = createConnectorProgressDialog($translate.instant('externalsync.creating'), response.data.iri, response.data.name);
        tabIdToConnectorProgressModalMapping.set(tabId, connectorProgressModal);
        return new BeforeUpdateQueryResult(BeforeUpdateQueryResultStatus.SUCCESS, 'created.connector', getCommandParameters(response));
    };

    const toRepairCommandResponse = (response, tabId) => {
        const connectorProgressModal = createConnectorProgressDialog($translate.instant('externalsync.repairing'), response.data.iri, response.data.name);
        tabIdToConnectorProgressModalMapping.set(tabId, connectorProgressModal);
        return new BeforeUpdateQueryResult(BeforeUpdateQueryResultStatus.SUCCESS, 'query.editor.repaired.connector', getCommandParameters(response));
    };

    const toDropCommandResponse = (response) => {
        return new BeforeUpdateQueryResult(BeforeUpdateQueryResultStatus.SUCCESS, 'externalsync.delete.success.msg', getCommandParameters(response));
    };

    const getBeforeUpdateQueryHandler = () => (query, tabId) => {
        return ConnectorsRestService.checkConnector(query)
            .then((response) => {
                if (!response.data.command) {
                    return toNoCommandResponse();
                }
                if (!response.data.hasSupport) {
                    return toHasNotSupport(response);
                }

                if (ConnectorCommand.CREATE === response.data.command) {
                    return toCreateCommandResponse(response, tabId);
                }

                if (ConnectorCommand.REPAIR === response.data.command) {
                    return toRepairCommandResponse(response, tabId);
                }

                if (ConnectorCommand.DROP === response.data.command) {
                    return toDropCommandResponse(response);
                }
            }).catch((error) => {
                // For some reason we couldn't check if this is a connector update, so just catch the exception,
                // to not stop the execution of query.
                logger.error('Checking connector error: ', error);
            });
    };

    const getExitPageConfirmMessage = (ongoingRequestsInfo) => {
        let exitPageConfirmMessage = "view.sparql-editor.leave_page.run_queries.confirmation.";
        if (!ongoingRequestsInfo || ongoingRequestsInfo.queriesCount < 1) {
            exitPageConfirmMessage += "none_queries_";
        } else if (ongoingRequestsInfo.queriesCount === 1) {
            exitPageConfirmMessage += "one_query_";
        } else {
            exitPageConfirmMessage += "queries_";
        }

        if (!ongoingRequestsInfo.updatesCount || ongoingRequestsInfo.updatesCount === 0) {
            exitPageConfirmMessage += "non_updates";
        } else if (ongoingRequestsInfo.updatesCount === 1) {
            exitPageConfirmMessage += "one_update";
        } else {
            exitPageConfirmMessage += "updates";
        }

        exitPageConfirmMessage += ".message";
        const params = {
            queriesCount: ongoingRequestsInfo.queriesCount,
            updatesCount: ongoingRequestsInfo.updatesCount,
        };
        return $translate.instant(exitPageConfirmMessage, params);
    };

    // Initialization and bootstrap
    const init = (clearYasguiState) => {
        // This script check is required, because of the following scenario:
        // I am in the SPARQL view;
        // Then I go to a different view and change the language;
        // Then I return to the SPARQL view. I will see that the Google chart and Pivot table will have their
        // original scripts loaded still.
        // THE FIX: Get all the scripts (if there are none, the correct language will be loaded). If there are
        // scripts, and they don't match those, which are loaded already, the page needs to reload when opening
        // the SPARQL view, otherwise the Google chart and Pivot table configs will be in the old language.
        const googleScripts = document.querySelectorAll(`script[src*="https://www.gstatic.com/"]`);
        if (googleScripts.length > 0) {
            const currentLang = $languageService.getLanguage();
            let searchTerm = 'module.js';
            if ('en' !== currentLang) {
                searchTerm = `module__${currentLang}.js`;
            }
            if (!Array.prototype.some.call(googleScripts, (script) => script.src.includes(searchTerm))) {
                location.reload();
                return;
            }
        }
        const principal = securityContextService.getAuthenticatedUser();
        $q.when($repositories.getPrefixes(activeRepository))
            .then((usedPrefixes) => {
                $scope.prefixes = usedPrefixes;
                setInferAndSameAs(principal);
                // check is there is a saved query or query url parameter and init the editor
                initViewFromUrlParams(clearYasguiState);
            });
        // TODO: we should also watch for changes in namespaces
        // scope.$watch('namespaces', function () {});
    };

    // =========================
    // Event handlers
    // =========================
    const subscriptions = [];

    const repositoryChangedHandler = (object) => {
        if (!object) {
            return;
        }
        activeRepository = $repositories.getActiveRepository();
        isOntopRepo = $repositories.isActiveRepoOntopType(object);
        if (LocalStorageAdapter.get(LSKeys.SPARQL_LAST_REPO) !== activeRepository) {
            init(true);
            persistLasstUsedRepository();
        } else {
            init(false);
        }
    };

    const beforeunloadHandler = (event) => {
        const ontotextYasguiElement = YasguiComponentDirectiveUtil.getOntotextYasguiElement(QUERY_EDITOR_ID);
        if (!ontotextYasguiElement) {
            return;
        }
        // If we set event.returnValue, the browser will prompt the user for confirmation to leave the page,
        // but we don't have a way to handle the user's choice.
        // Therefore, we can't take any action, so we simply proceed to abort all requests.
        ontotextYasguiElement.abortAllRequests().then(() => {});
    };

    window.addEventListener('beforeunload', beforeunloadHandler);

    const confirmIfHaveRunQuery = (ongoingRequestsInfo) => new Promise((resolve, reject) => {
        if (!ongoingRequestsInfo || ongoingRequestsInfo.queriesCount < 1 && ongoingRequestsInfo.updatesCount < 1) {
            resolve();
            return;
        }

        const title = $translate.instant('common.confirm');
        const message = decodeHTML(getExitPageConfirmMessage(ongoingRequestsInfo));
        ModalService.openSimpleModal({
            title,
            message,
            warning: true,
        }).result.then(function() {
            resolve();
        }, function() {
            reject(new CancelAbortingQuery());
        });
    });
    let queriesAreCanceled = undefined;
    const locationChangeHandler = (eventPayload) => {
        if (internallyReloaded) {
            internallyReloaded = false;
            return;
        }
        const ontotextYasguiElement = YasguiComponentDirectiveUtil.getOntotextYasguiElement(QUERY_EDITOR_ID);
        if (!ontotextYasguiElement || queriesAreCanceled) {
            return;
        }

        const url = new URL(eventPayload.newUrl);
        const newUrl = url.pathname + url.search + url.hash;
        eventPayload.cancelNavigation();
        // First, we check if there are any ongoing requests initiated by the user.
        // If the user has ongoing requests, we request confirmation to abort them.
        // If the user confirms or there are no ongoing requests, we call the "abortAllRequests" method. This method will abort all requests.
        ontotextYasguiElement
            .getOngoingRequestsInfo()
            .then((hasRunQuery) => confirmIfHaveRunQuery(hasRunQuery))
            .then(() => ontotextYasguiElement.abortAllRequests())
            .then(() => {
                queriesAreCanceled = true;
                navigateTo(newUrl)();
            })
            .catch((error) => {
                if (!(error instanceof CancelAbortingQuery)) {
                    logger.error(error);
                    queriesAreCanceled = true;
                    navigateTo(newUrl)();
                }
            });
    };

    subscriptions.push(
        ServiceProvider.get(EventService).subscribe(EventName.NAVIGATION_START, (eventPayload) => locationChangeHandler(eventPayload)),
    );

    const removeAllListeners = () => {
        subscriptions.forEach((subscription) => subscription());
        window.removeEventListener('beforeunload', beforeunloadHandler);
    };

    const finalizeAndDestroy = () => {
        persistLasstUsedRepository();
        removeAllListeners();
    };

    const persistLasstUsedRepository = () => {
        // The active repository is set when the controller is initialized and when the repository is changed.
        // It holds the actual repository when the YASGUI is initialized. DON'T use runtime fetching of the actual repository here, because there is a scenario:
        // 1. Open a tab with the SPARQL view open;
        // 2. Open the SPARQL view in another tab and execute a query;
        // 3. Switch to the repositories view and change the repository;
        // 4. Switch back to the SPARQL view, and the YASR is not cleared.
        // The problem occurs because of the second tab. When the repository is changed, its ID is persisted to local storage, which triggers the "storage" event to be fired.
        // In the main controller, a listener has been registered to listen to that event and refresh the page outside of the Angular scope.
        // Reloading the page triggers the destruction of the component and persistence of the active repository. The reloading of the page is out of the Angular scope, so the "activeRepository"
        // holds the real repository when the YASGUI is created. If we use $$repositories.getActiveRepository(), the new value will be fetched, which in this case will be incorrect.
        LocalStorageAdapter.set(LSKeys.SPARQL_LAST_REPO, activeRepository);
    };

    subscriptions.push(
        $scope.$on('language-changed', function() {
            location.reload();
        }),
    );

    const onLanguageChange = () => {
        // Do nothing on language change
    };

    const showLanguageChangeConfirmation = () => {
        return new Promise((resolve) => {
            ModalService.openSimpleModal({
                title: $translate.instant('query.editor.language.change.warning.title'),
                message: $translate.instant('query.editor.reload.page.warning'),
                warning: true,
            }).result.then(function() {
                resolve(true);
            }, function() {
                resolve(false);
            });
        });
    };

    const languageContextService = ServiceProvider.get(LanguageContextService);
    const onLanguageChangeSubscription = languageContextService.onSelectedLanguageChanged(onLanguageChange, showLanguageChangeConfirmation);

    // Subscribe to language change and ask to reload the page
    subscriptions.push(onLanguageChangeSubscription);

    // Deregister the watcher when the scope/directive is destroyed
    subscriptions.push($scope.$on('$destroy', finalizeAndDestroy));

    // Wait until the active repository object is set, otherwise "canWriteActiveRepo()" may return a wrong result and the "ontotext-yasgui"
    // readOnly configuration may be incorrect.
    subscriptions.push($scope.$watch($scope.getActiveRepositoryObject, repositoryChangedHandler));
}
